import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * TrackOG / We24Seven — third-party visitor-tracking pixel.
 *
 * Loads EXCLUSIVELY on the public homepage route (`/`). This component is
 * intentionally mounted from `pages/Home.jsx` only — it is NOT injected in
 * `index.html`, `App.js`, `Header.jsx`, or `Footer.jsx`, so it will never
 * fire on:
 *   - /app/login, /app/signup, or any /app/* authenticated route
 *   - /pricing, /features, /about, /services, /community, /blog, /contact
 *   - /admin/* (login, dashboard, conversions, publisher-links)
 *   - Any checkout / payment surface
 *
 * The script is a plain <script src="..."> with NO query params, NO payload,
 * NO postMessage, NO PII — we only ever mount and unmount the tag itself, so
 * there is no code path in this file that could leak email / phone / user ID /
 * Firebase token / health data / Razorpay data / payment status.
 *
 * Duplicate-load guard: a global `window.__femcareTrackOGMounted` flag ensures
 * the tag is injected exactly once per browser lifetime. React StrictMode
 * double-mount in dev, or the SPA re-rendering `<Home/>`, will NOT inject a
 * second copy. If the user later navigates away from `/`, the tag is removed
 * so a subsequent return to `/` does not accidentally re-execute the script.
 */
const TRACKOG_SRC = "https://cdn-edge.trackog.net/rtt/3tUB3Q7tWm.js?v=1";
const TAG_ID = "femcare-trackog-homepage";

export default function TrackOGHomepageScript() {
    const { pathname } = useLocation();

    useEffect(() => {
        // Absolute homepage-only gate — defence in depth even though this
        // component is only rendered by <Home/>.
        if (pathname !== "/") return;
        if (typeof window === "undefined") return;

        // Guard against duplicate insertion (StrictMode double-mount, HMR,
        // SPA re-render, or a stale tag left over from a previous visit).
        if (window.__femcareTrackOGMounted) return;
        if (document.getElementById(TAG_ID)) {
            window.__femcareTrackOGMounted = true;
            return;
        }

        const s = document.createElement("script");
        s.id = TAG_ID;
        s.src = TRACKOG_SRC;
        s.async = true;
        // Explicitly NO onload/onerror callbacks and NO attributes carrying
        // user data — this stays a passive third-party tag.
        document.head.appendChild(s);
        window.__femcareTrackOGMounted = true;

        // Cleanup on route-away from `/`: remove the tag so the SPA does not
        // execute it a second time when the user comes back to `/`.
        return () => {
            const existing = document.getElementById(TAG_ID);
            if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
            try { delete window.__femcareTrackOGMounted; } catch { window.__femcareTrackOGMounted = false; }
        };
    }, [pathname]);

    return null;   // no UI — this component renders nothing
}
